import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {
  CHEAP_PRODUCT_ID,
  EXPENSIVE_PRODUCT_ID,
  FREEBIE_PRODUCT_ID,
  getDisplayPriceForProduct,
  getProductIdForVideoDownloadsForNonPremiumUsers,
  MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT,
  VideoDesignSizeOptionType,
} from '@Libraries/poster-download-library';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Text, TextSize} from '@Components/text';
import {type BulletItem, QualityItem} from '@Components/download-panel/components/quality-item/quality-item';
import {TagType} from '@Components/tag';
import {getWidthAndHeightInPixels, isExpensiveVideoPoster} from '@Libraries/poster-object.library';
import styles from './download-video-quality-option.module.scss';

export function DownloadVideoQualityOption(): ReactElement {
  const {productsData, appleDownloadProducts, selectedPages, duration, setDuration, needsPurchase, setVideoScale, videoScale, selectedProductId, setSelectedProductId, posterObject, setScale} =
    useDownloadPanelData();
  const showCheapProduct = selectedPages.length <= MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT && !isExpensiveVideoPoster(posterObject);
  const CHEAP_PRODUCT = productsData?.find((productsData) => productsData.id === String(CHEAP_PRODUCT_ID));
  const EXPENSIVE_PRODUCT = productsData?.find((productsData) => productsData.id === String(EXPENSIVE_PRODUCT_ID));

  const setScaleForHD = (): void => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    const scaledDimension = 1280;
    if (convertedWidth < convertedHeight) {
      setScale(scaledDimension / convertedHeight);
    } else {
      setScale(scaledDimension / convertedWidth);
    }
  };

  useEffect((): void => {
    if (isExpensiveVideoPoster(posterObject) && selectedProductId === CHEAP_PRODUCT_ID) {
      const productIdForVideoDownloadsForNonPremiumUsers = getProductIdForVideoDownloadsForNonPremiumUsers(videoScale, duration, selectedPages.length, posterObject);
      setSelectedProductId(productIdForVideoDownloadsForNonPremiumUsers);
      return;
    }

    if (!isExpensiveVideoPoster(posterObject) && selectedProductId === EXPENSIVE_PRODUCT_ID) {
      setSelectedProductId(CHEAP_PRODUCT_ID);
    }
  }, [posterObject]);

  useEffect(() => {
    const productIdForVideoDownloadsForNonPremiumUsers = getProductIdForVideoDownloadsForNonPremiumUsers(videoScale, duration, selectedPages.length, posterObject);
    if (productIdForVideoDownloadsForNonPremiumUsers !== selectedProductId) {
      setSelectedProductId(productIdForVideoDownloadsForNonPremiumUsers);
    }
  }, [videoScale, duration, selectedPages]);

  const getExpensiveProductTagText = (): string | undefined => {
    if (needsPurchase) {
      if (showCheapProduct) {
        return CHEAP_PRODUCT?.price ?? undefined;
      }
      return getDisplayPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts) ?? undefined;
    }
    return undefined;
  };

  const getBulletsForFreeProduct = (): BulletItem[] => {
    const items = [
      {
        text: window.i18next.t('pmwjs_20_seconds_limit'),
        showSuccessBullet: false,
      },
    ];

    if (selectedPages.length > 1) {
      items.push({
        text: window.i18next.t('pmwjs_x_page_limit', {limit: MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT + 1}),
        showSuccessBullet: false,
      });
    }

    return items;
  };

  return (
    <div className={`flex flex-v-row gap-2`}>
      <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_quality')} />
      <div className={`flex-h-row gap-3`}>
        <QualityItem
          bulletsClassName={styles.bulletsContainer}
          id={`${FREEBIE_PRODUCT_ID}`}
          tagText={needsPurchase ? window.i18next.t('pmwjs_free') : undefined}
          onClick={(): void => {
            setSelectedProductId(FREEBIE_PRODUCT_ID);
            if (duration > 20) {
              setDuration(20);
            }

            if (videoScale === VideoDesignSizeOptionType.UHD_4K || videoScale === VideoDesignSizeOptionType.FULL_HD) {
              setVideoScale(VideoDesignSizeOptionType.HD);
              setScaleForHD();
            }
          }}
          text={window.i18next.t('pmwjs_basic_poster_download')}
          isSelected={selectedProductId === FREEBIE_PRODUCT_ID}
          bullets={getBulletsForFreeProduct()}
        />
        <QualityItem
          id={`${showCheapProduct ? CHEAP_PRODUCT_ID : EXPENSIVE_PRODUCT_ID}`}
          tagClassName={styles.secondaryTag}
          tagType={TagType.SECONDARY_LIGHT}
          tagText={getExpensiveProductTagText()}
          text={window.i18next.t('pmwjs_high')}
          onClick={(): void => {
            if (showCheapProduct) {
              setSelectedProductId(CHEAP_PRODUCT_ID);
              return;
            }
            setSelectedProductId(EXPENSIVE_PRODUCT_ID);
          }}
          bullets={[
            {
              text: window.i18next.t('pmwjs_no_watermark'),
              showSuccessBullet: true,
            },
            {
              text: window.i18next.t('pmwjs_no_limits'),
              showSuccessBullet: true,
            },
          ]}
          isSelected={showCheapProduct ? selectedProductId === CHEAP_PRODUCT_ID : selectedProductId === EXPENSIVE_PRODUCT_ID}
        />
      </div>
    </div>
  );
}
